/**
 * Configuración — hub administrativo del sistema, con el layout de settings
 * de Bankivo: navegación lateral agrupada por secciones a la izquierda y la
 * herramienta seleccionada incrustada a la derecha (prop enHub: las
 * pantallas ocultan su encabezado grande y muestran el compacto).
 *
 * Las entradas se muestran según el programa del rol (el backend re-valida
 * en permisos.py). "Limpiar producto" va en la Zona de riesgo, en rojo: es
 * un borrado definitivo e irreversible. En móvil la navegación se vuelve
 * una fila de chips desplazable.
 *
 * Sustituye las 7 entradas del grupo Configuración del menú; las URLs
 * viejas siguen abriendo cada pantalla suelta (sin enHub).
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import ConfiguracionSistema from "modules/configuracion/configuracionsistema";
import CatalogosConfig from "modules/configuracion/catalogosconfig";
import CamposExtraConfig from "modules/configuracion/camposextraconfig";
import UsuariosRoles from "modules/configuracion/usuariosroles";
import AbonosCapital from "modules/configuracion/abonoscapital";
import DescuentosInteres from "modules/configuracion/descuentosinteres";
import CambioSucursalPrestamo from "modules/configuracion/cambiosucursalprestamo";
import LimpiarProducto from "modules/configuracion/limpiarproducto";
import Documentos from "modules/documentacion/documentos";
import MonedasConfig from "modules/configuracion/monedasconfig";
import PortalClientes from "modules/configuracion/portalclientes";

const DANGER = "#B3261E";

export default function ConfiguracionHub() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  const tieneConfig = tienePermisosModulo(roleDetalles, "configuracion");

  // Navegación lateral agrupada (estilo Bankivo NAV_SECTIONS).
  const SECCIONES = [
    {
      grupo: "General",
      items: [
        {
          clave: "sistema",
          icono: SettingsOutlinedIcon,
          titulo: "Sistema",
          visible: tieneConfig,
          componente: <ConfiguracionSistema enHub />,
        },
        {
          clave: "catalogos",
          icono: CategoryOutlinedIcon,
          titulo: "Catálogos",
          visible: tieneConfig,
          componente: <CatalogosConfig enHub />,
        },
        {
          clave: "camposextra",
          icono: DynamicFormOutlinedIcon,
          titulo: "Campos Adicionales",
          visible: tieneConfig,
          componente: <CamposExtraConfig enHub />,
        },
        {
          clave: "documentacion",
          icono: DescriptionOutlinedIcon,
          titulo: "Documentos",
          // Programa "documentacion" = configurar plantillas (esta pestaña y
          // guardar/importar/eliminar en el backend). "documentos" solo
          // genera documentos (expediente) y ya no abre el editor.
          visible: tienePermisosModulo(roleDetalles, "documentacion"),
          componente: <Documentos enHub />,
        },
        {
          clave: "monedas",
          icono: CurrencyExchangeOutlinedIcon,
          titulo: "Monedas y Tasa",
          // Programa propio ("monedas"): registrar la tasa del día es una
          // tarea diaria de caja, no de administración del sistema, así que
          // puede darse a roles que no tienen "configuracion".
          visible: tienePermisosModulo(roleDetalles, "monedas"),
          componente: <MonedasConfig enHub />,
        },
        {
          clave: "portalclientes",
          icono: PhoneIphoneOutlinedIcon,
          titulo: "Portal de Clientes",
          // Activar el portal es una decisión del negocio, no una tarea de
          // caja: va con "configuracion".
          visible: tieneConfig,
          componente: <PortalClientes enHub />,
        },
      ],
    },
    {
      grupo: "Usuarios y roles",
      items: [
        {
          clave: "usuarios",
          icono: GroupOutlinedIcon,
          titulo: "Usuarios",
          visible: tieneConfig,
          componente: <UsuariosRoles enHub vista="usuarios" />,
        },
        {
          clave: "roles",
          icono: AdminPanelSettingsOutlinedIcon,
          titulo: "Roles",
          visible: tieneConfig,
          componente: <UsuariosRoles enHub vista="roles" />,
        },
      ],
    },
    {
      grupo: "Préstamos",
      items: [
        {
          clave: "abonos",
          icono: SavingsOutlinedIcon,
          titulo: "Abonos a Capital",
          visible: tienePermisosModulo(roleDetalles, "ares8060"),
          componente: <AbonosCapital enHub />,
        },
        {
          clave: "intereses",
          icono: PercentOutlinedIcon,
          titulo: "Intereses",
          visible: tienePermisosModulo(roleDetalles, "ares3030"),
          componente: <DescuentosInteres enHub />,
        },
        {
          clave: "sucursal",
          icono: StoreOutlinedIcon,
          titulo: "Cambio de Sucursal",
          visible: tienePermisosModulo(roleDetalles, "ares8090"),
          componente: <CambioSucursalPrestamo enHub />,
        },
      ],
    },
    {
      grupo: "Zona de riesgo",
      items: [
        {
          clave: "limpiar",
          icono: DeleteForeverOutlinedIcon,
          titulo: "Limpiar Producto",
          visible: tienePermisosModulo(roleDetalles, "ares8080"),
          peligro: true,
          componente: <LimpiarProducto enHub />,
        },
      ],
    },
  ];

  const visibles = SECCIONES.map((s) => ({
    ...s,
    items: s.items.filter((i) => i.visible),
  })).filter((s) => s.items.length);
  const todas = visibles.flatMap((s) => s.items);

  const [activa, setActiva] = useState(null);
  const item = todas.find((i) => i.clave === activa) || todas[0] || null;

  const itemNav = (i) => {
    const seleccionada = item && i.clave === item.clave;
    const color = i.peligro ? DANGER : FP.color.navy;
    return (
      <ButtonBase
        key={i.clave}
        onClick={() => setActiva(i.clave)}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1,
          px: 1.2,
          py: 0.8,
          borderRadius: 1.8,
          textAlign: "left",
          bgcolor: seleccionada ? `${color}14` : "transparent",
          "&:hover": { bgcolor: seleccionada ? `${color}14` : FP.color.surfaceAlt },
        }}
      >
        <i.icono
          sx={{
            fontSize: 19,
            color: seleccionada ? color : FP.color.textSecondary,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.84rem",
            fontWeight: seleccionada ? 800 : 600,
            color: seleccionada ? color : FP.color.textPrimary,
          }}
        >
          {i.titulo}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Configuración"
        subtitle="Herramientas administrativas del sistema — acceso restringido por rol"
      />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {/* Navegación lateral angosta (chips desplazables en móvil) */}
        <Box sx={{ width: { xs: "100%", md: 200 }, flexShrink: 0 }}>
          <Box sx={{ display: { xs: "none", md: "block" }, position: "sticky", top: 76 }}>
            {visibles.map((s) => (
              <Box key={s.grupo} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    px: 1.5,
                    pb: 0.6,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: FP.color.textMuted,
                  }}
                >
                  {s.grupo}
                </Typography>
                <Stack spacing={0.3}>{s.items.map(itemNav)}</Stack>
              </Box>
            ))}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              display: { xs: "flex", md: "none" },
              overflowX: "auto",
              pb: 1,
            }}
          >
            {todas.map((i) => (
              <Chip
                key={i.clave}
                label={i.titulo}
                onClick={() => setActiva(i.clave)}
                variant={item && i.clave === item.clave ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  flexShrink: 0,
                  ...(item && i.clave === item.clave
                    ? {
                        bgcolor: i.peligro ? DANGER : FP.color.navy,
                        color: "#fff",
                        "&:hover": { bgcolor: i.peligro ? DANGER : FP.color.navySoft },
                      }
                    : { color: i.peligro ? DANGER : FP.color.textPrimary }),
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Herramienta seleccionada, incrustada */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            border: `1px solid ${FP.color.border}`,
            borderRadius: 2.5,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          {item ? item.componente : null}
        </Box>
      </Box>
    </Box>
  );
}
